import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { NavigationEnd, Router } from '@angular/router';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { MapService } from '@barthauer/abc/graphics/adapters/open-layers';
import {
  BasysMapFrameService,
  PanelComponent,
  RoutingService,
  ViewStateChangeArgType,
} from '@barthauer/basys/map-frame';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { MediaChange, MediaObserver } from 'ngx-flexible-layout';
import { DataSourceFilter, FilterType } from '../../models/componentDataSource.model';
import { AppService } from '../../services/app.service';
import { FilterButton } from '../../shared/helpers';
import { IMobileWork, MobileWorkViewModel } from '../../viewModels/mobileData.viewModel';
import { WorkNavigationContext, WorksDataContext, WorkState, WorkStateChangeArgType } from '../works.model';
import { WorksService } from '../works.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';

@Component({
  selector: 'app-work-list',
  templateUrl: './work-list.component.html',
  styleUrls: ['./work-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WorkListComponent extends PanelComponent implements OnInit, OnDestroy, AfterViewInit {
  public res = ResourceSet.Empty;
  public isWorkListFilterRemoved: boolean = false;
  @ViewChild('workListScrollViewPort', { static: true }) workListScrollViewPort: CdkVirtualScrollViewport;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  readonly VIEWPORTHEIGHTVERTICALMARGIN_DEFAULT: number = 87; //header-bar + sort bar height
  readonly VIEWPORTHEIGHTVERTICALMARGIN_XS_WITH_FILTERCHIPS: number = 118; //header-bar + sort bar + "chips bar" height = 117 on desktop, 118 on mobile is better :-/
  private detailWorkOffset: number;
  private currentViewPortSize: string;
  private workStateFilters: Map<any, DataSourceFilter>;
  AppService = AppService;
  scrollViewPortElementName: string = 'workListScrollViewPort';
  public dataContext: WorksDataContext;
  public selectedWorkStates: string[] = [
    WorkState.Beauftragt.toString(),
    WorkState.Teilerledigt.toString(),
    WorkState.Erledigt.toString(),
  ];
  public workStateButtons: FilterButton[] = [];
  public hasMtObjectNotes = false;

  public get scrollViewPortHeightVerticalMargin(): number {
    if (this.dataContext && this.dataContext.dataSource.filters.length > 0) {
      return this.currentViewPortSize === 'xs'
        ? this.VIEWPORTHEIGHTVERTICALMARGIN_XS_WITH_FILTERCHIPS
        : this.VIEWPORTHEIGHTVERTICALMARGIN_DEFAULT;
    } else {
      return this.VIEWPORTHEIGHTVERTICALMARGIN_DEFAULT;
    }
  }

  constructor(
    private worksService: WorksService,
    private resourceService: ResourceManagerService,
    frame: BasysMapFrameService,
    mediaObserver: MediaObserver,
    router: Router,
    private mapService: MapService,
    private routingService: RoutingService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
    super.init(frame);
    this.workStateFilters = new Map<any, DataSourceFilter>();

    this.subscriptions.push(
      frame.lib.onViewStateChanged().subscribe((args) => {
        switch (args.type) {
          case ViewStateChangeArgType.BottomSheetPanelOpened: {
            this.workListScrollViewPort.checkViewportSize();
            break;
          }
        }
        // this.calculateViewPortSize();
      }),
    );

    this.subscriptions.push(
      worksService.onWorkStateChanged().subscribe((args) => {
        switch (args.type) {
          case WorkStateChangeArgType.WorksFilterApplied: {
            this.ensureViewPortSize();
            break;
          }
        }
      }),
    );

    this.subscriptions.push(
      worksService.onDataContextSent().subscribe((context) => {
        this.currentAggregatedModeCategoryHeaderText = null;
        this.dataContext = context;
        this.dataContext.dataSource.sort = this.sort;
        this.sortData();
        this.ensureWorkStateFilter(WorkState.Beauftragt.toString());
        this.ensureWorkStateFilter(WorkState.Teilerledigt.toString());
        this.ensureWorkStateFilter(WorkState.Erledigt.toString());
        this.selectedWorkStates = this.dataContext.selectedWorkStates;
        this.hasMtObjectNotes = this.existsMtObjectsWithNote();
        this.initSortingAccessor(this.dataContext.dataSource.internalDataSource);
        this.cdr.detectChanges();
      }),
    );
    this.subscriptions.push(
      router.events.subscribe((e) => {
        if (e instanceof NavigationEnd && routingService.getCurrentConfig().designation === AppService.Route_WorkList) {
          this.workListScrollViewPort.scrollToOffset(this.detailWorkOffset, 'auto');
          this.cdr.detectChanges();
        }
      }),
    );
    this.subscriptions.push(
      // BUG
      mediaObserver.asObservable().subscribe((changes: MediaChange[]) => {
        this.currentViewPortSize = changes[0].mqAlias;
        this.ensureViewPortSize();
      }),
    );
  }

  //needed ?!
  //calculateViewPortSize() {
  // const bottomSheetElement = document.querySelector(
  //   '.bottom-sheet-container > .mat-bottom-sheet-container'
  // ) as HTMLElement;
  // if (bottomSheetElement) {
  //   const startPos = bottomSheetElement.offsetTop;
  //   const newTop = startPos;
  //   const screenHeight = window.innerHeight;
  //   const workListElement = document.querySelector('.workListScrollViewPort') as HTMLElement;
  //   if (workListElement) {
  //     workListElement.style.setProperty('height', screenHeight - newTop - 151 + 'px', 'important');
  //   }
  // }
  //}
  ngAfterViewInit(): void {
    //this.calculateViewPortSize();
  }

  async ngOnInit() {
    super.ngOnInit();

    this.res = await this.resourceService.getResourceSet('abc-maintenance-opera');

    this.workStateButtons = [
      {
        id: WorkState.Beauftragt.toString(),
        icon: this.res.svgUrl('StateOpen-Image'),
        hint: 'Beauftragt / Geplant / Anstehend',
        predicate: (obj) =>
          obj.hasState(WorkState.Beauftragt) || obj.hasState(WorkState.Geplant) || obj.hasState(WorkState.Anstehend),
        badge: null,
      },
      {
        id: WorkState.Teilerledigt.toString(),
        icon: this.res.svgUrl('StatePartial-Image'),
        hint: 'Teilerledigt',
        predicate: (obj) => obj.hasState(WorkState.Teilerledigt),
        badge: null,
      },
      {
        id: WorkState.Erledigt.toString(),
        icon: this.res.svgUrl('StateDone-Image'),
        hint: 'Erledigt',
        predicate: (obj) => obj.hasState(WorkState.Erledigt),
        badge: null,
      },
    ];
  }

  private ensureViewPortSize(): void {
    super.updateSize();
  }

  private ensureWorkStateFilter(workStateString: string): void {
    if (!this.workStateFilters.has(workStateString)) {
      const ds = this.createAndApplyWorkStateFilter(
        workStateString,
        this.workStateButtons.find((x) => x.id === workStateString).predicate,
      );
      this.workStateFilters.set(workStateString, ds);
    }
  }

  private createAndApplyWorkStateFilter(
    workStateString: string,
    predicate: (obj: IMobileWork) => boolean,
  ): DataSourceFilter {
    const f: DataSourceFilter = new DataSourceFilter();
    f.type = FilterType.Predicate;
    f.predicate = predicate;
    f.displayText = workStateString; //used as key at the moment :/
    f.visible = false;

    this.dataContext.applyFilterObject(f);
    return f;
  }

  public removeFilter(filter: DataSourceFilter): void {
    if (filter.type === FilterType.Action) {
      this.dataContext.isInAggregatedMode = false;
      this.currentAggregatedModeCategoryHeaderText = null;
      filter.action();
    }
    this.dataContext.dataSource.removeFilter(filter);
    this.ensureViewPortSize();
    this.workListScrollViewPort.checkViewportSize();
    this.sortData();
    this.isWorkListFilterRemoved = true;
  }

  public navigateToWorkDetail(workIndex: number): void {
    this.worksService.isResourceListVisible = false;
    this.detailWorkOffset = this.workListScrollViewPort.measureScrollOffset();
    this.routingService.navigateName(AppService.Route_WorkDetail).then((succ) => {
      if (succ) {
        const context: WorkNavigationContext = new WorkNavigationContext();
        context.workDataContext = this.dataContext;
        context.workIndex = workIndex;
        this.worksService.refreshNavigationContext(context);
      }
    });
  }

  //needed?!
  public navigateToWorkDetailByWork(work: MobileWorkViewModel, wIndex?: number): void {
    let workId: string = work?.id;
    this.detailWorkOffset = this.workListScrollViewPort.measureScrollOffset();
    this.routingService.navigateName(AppService.Route_WorkDetail).then((succ) => {
      if (succ) {
        const context: WorkNavigationContext = new WorkNavigationContext();
        context.workDataContext = this.dataContext;
        context.workId = workId;
        context.workIndex = wIndex;
        this.worksService.refreshNavigationContext(context);
      }
    });
  }

  public workStateFilterClick(e): void {
    this.dataContext.selectedWorkStates = this.selectedWorkStates;
    const toogled: boolean = this.dataContext.selectedWorkStates.some((x) => x === e.itemData.id);
    this.workStateFilters.get(e.itemData.id).enabled = toogled;
    this.dataContext.updateFilter();
    this.sortData();
  }

  public selectWorkInMap(work: MobileWorkViewModel): void {
    this.mapService.navigateToObjects([
      {
        id: work.object.drawableObjectId,
        objectType: work.object.drawableObjectType,
      },
    ]);
    this.frame.lib.closeBottomSheet();
  }

  public trackById(index, item) {
    return item.id;
  }

  public sortData() {
    if (!this.dataContext.isInAggregatedMode) {
      this.dataContext.dataSource.internalDataSource.sortData(
        this.dataContext.dataSource.internalDataSource.filteredData,
        this.dataContext.dataSource.sort,
      );
    }
  }

  private existsMtObjectsWithNote(): boolean {
    let retHas = false;
    if (this.dataContext?.dataSource?.internalDataSource) {
      const works = this.dataContext.dataSource.internalDataSource.data as MobileWorkViewModel[];
      if (works) {
        retHas = works.findIndex((w) => !!w.note) > -1;
      }
    }

    return retHas;
  }

  private initSortingAccessor(dataSource: MatTableDataSource<IMobileWork, MatPaginator>): void {
    dataSource.sortingDataAccessor = (item: MobileWorkViewModel, property: string) => {
      const value = item[property];

      if (value === null) {
        if ('desc' == this.sort.direction) return '\u0000' + '\u0000' + '\u0000';
        return '\uFFFF' + '\uFFFF' + '\uFFFF';
      }

      return value;
    };
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  private currentAggregatedModeCategoryHeaderText: string = null;

  mustShowCategoryHeader(work: MobileWorkViewModel): boolean {
    let mustShowHeader = false;

    if (this.dataContext.isInAggregatedMode) {
      const newHeaderText = work.aggregatedModeCategoryHeaderText;

      if (newHeaderText !== this.currentAggregatedModeCategoryHeaderText) {
        this.currentAggregatedModeCategoryHeaderText = newHeaderText;
      }

      mustShowHeader = true;
    }

    return mustShowHeader;
  }
}
